import { useCallback } from 'react'
import log from 'loglevel'
import {
  Int32Value,
  StringValue,
  BoolValue,
} from 'google-protobuf/google/protobuf/wrappers_pb'
import { ControlServicePromiseClient } from '@trustero/trustero-api-web/lib/model/control_grpc_web_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { ControlStatusRequest } from '@trustero/trustero-api-web/lib/model/model_pb'
import {
  CONTROL_STATUS,
  ControlStatusValue,
  CreateControlTestProcedureRequest,
  DeleteControlTestProcedureRequest,
  LinkControlPoliciesRequest,
  UnlinkControlPoliciesRequest,
  UpdateControlFieldsRequest,
  UpdateControlSuggestedEvidenceRequest,
  UpdateControlSuggestedTestsRequest,
  UpdateControlFrameworksRequest,
  UpdateControlObjectiveRequest,
  UpdateControlProcedureRequest,
  UpdateControlGeneralGuidanceRequest,
  UpdateControlLabelsRequest,
  AddControlLabelRequest,
  RemoveControlLabelRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import {
  Identifier,
  MODEL_TYPE,
  RESPONSIBILITY,
  ResponsibilityValue,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import { useAuthorizedGrpcClient } from '../../../../adapter'
import { useGrpcRevalidateByMethod } from '../../useGrpcMutate'
import { useInvalidatePoliciesCache } from '../../policy/usePolicies'
import { useInvalidateControlsCache } from './useControls'
import { useInvalidateControlTestStaleness } from './useControlTestStaleness'
import { useRefreshAfterTPRSave } from './useControlVersions'

export const useUpdateControlFields = (): ((args: {
  controlId: string
  objective?: string
  procedure?: string
  note?: string
  status?: CONTROL_STATUS
  ownerEmail?: string
  responsibility?: RESPONSIBILITY
  notApplicableReason?: string
  vendorId?: string
  name?: string
  modelId?: string
  customStatusId?: string
  active?: boolean
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()
  const invalidateStaleness = useInvalidateControlTestStaleness()

  return useCallback(
    async ({
      controlId,
      objective,
      procedure,
      note,
      status,
      ownerEmail,
      responsibility,
      notApplicableReason,
      vendorId,
      name,
      modelId,
      customStatusId,
      active,
    }) => {
      try {
        const request = new UpdateControlFieldsRequest().setControlId(controlId)
        if (objective !== undefined) {
          request.setObjective(new StringValue().setValue(objective))
        }
        if (procedure !== undefined) {
          request.setProcedure(new StringValue().setValue(procedure))
        }
        if (note !== undefined) {
          request.setNote(new StringValue().setValue(note))
        }
        if (status !== undefined) {
          request.setStatus(new ControlStatusValue().setValue(status))
        }
        if (ownerEmail !== undefined) {
          request.setOwnerEmail(new StringValue().setValue(ownerEmail))
        }
        if (responsibility !== undefined) {
          request.setResponsibility(
            new ResponsibilityValue().setValue(responsibility),
          )
        }
        if (notApplicableReason !== undefined) {
          request.setNotApplicableReason(
            new StringValue().setValue(notApplicableReason),
          )
        }
        if (vendorId !== undefined) {
          request.setVendorId(new StringValue().setValue(vendorId))
        }
        if (name !== undefined) {
          request.setName(new StringValue().setValue(name))
        }
        if (modelId !== undefined) {
          request.setModelId(new StringValue().setValue(modelId))
        }
        if (customStatusId !== undefined) {
          request.setCustomStatusId(new StringValue().setValue(customStatusId))
        }
        if (active !== undefined) {
          request.setActive(new BoolValue().setValue(active))
        }
        await client.updateControlFields(request)
      } catch (error) {
        log.error('Error updating control fields', error)
        throw error
      } finally {
        // This is the other path that can rewrite the objective, so it moves the
        // objective hash exactly as useUpdateControlObjective does.
        await Promise.all([
          invalidate(),
          objective !== undefined ? invalidateStaleness() : Promise.resolve(),
        ])
      }
    },
    [client, invalidate, invalidateStaleness],
  )
}

/**
 * Status-only edits go to Model/SetControlStatus rather than UpdateControlFields.
 *
 * The two write the same control_models row through the same helper, but they are gated
 * differently: SetControlStatus requires CAN_UPDATE_CONTROL_STATUS, where UpdateControlFields
 * requires the umbrella CAN_UPDATE_CONTROLS that also admits editing every other field. Routing
 * status here is what lets a role hold the status without the rest -- auditors, today.
 *
 * It also clears the linkages of a control moved to Not Applicable, which UpdateControlFields
 * does not. Marking a control N/A carries a responsibility and a reason, so it stays on
 * UpdateControlFields; this path is for a plain status change.
 */
export const useSetControlStatus = (): ((args: {
  controlId: string
  status: CONTROL_STATUS
  customStatusId?: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ModelPromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, status, customStatusId }) => {
      try {
        const request = new ControlStatusRequest()
          .setId(
            new Identifier()
              .setModeltype(MODEL_TYPE.CONTROL)
              .setModelid(controlId),
          )
          .setStatus(status)
          // Plain proto string, and the server writes it on every call: passing nothing
          // clears the custom status, which is what picking a standard one means.
          .setCustomStatusId(customStatusId ?? '')
        await client.setControlStatus(request)
      } catch (error) {
        log.error('Error setting control status', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlSuggestedEvidence = (): ((args: {
  controlId: string
  suggestedEvidence: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()
  const invalidateStaleness = useInvalidateControlTestStaleness()

  return useCallback(
    async ({ controlId, suggestedEvidence }) => {
      try {
        const request = new UpdateControlSuggestedEvidenceRequest()
          .setControlId(controlId)
          .setSuggestedEvidence(suggestedEvidence)
        await client.updateControlSuggestedEvidence(request)
      } catch (error) {
        log.error('Error updating suggested evidence', error)
        throw error
      } finally {
        // Required evidence feeds the guidance hash.
        await Promise.all([invalidate(), invalidateStaleness()])
      }
    },
    [client, invalidate, invalidateStaleness],
  )
}

export const useUpdateControlGeneralGuidance = (): ((args: {
  controlId: string
  generalGuidance: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()
  const invalidateStaleness = useInvalidateControlTestStaleness()

  return useCallback(
    async ({ controlId, generalGuidance }) => {
      try {
        const request = new UpdateControlGeneralGuidanceRequest()
          .setControlId(controlId)
          .setGeneralGuidance(generalGuidance)
        await client.updateControlGeneralGuidance(request)
      } catch (error) {
        log.error('Error updating general guidance', error)
        throw error
      } finally {
        await Promise.all([invalidate(), invalidateStaleness()])
      }
    },
    [client, invalidate, invalidateStaleness],
  )
}

export const useUpdateControlSuggestedTests = (): ((args: {
  controlId: string
  suggestedTests: string
  testProcedureId?: string
  title?: string
  position?: number
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const refreshAfterTPRSave = useRefreshAfterTPRSave()

  return useCallback(
    async ({ controlId, suggestedTests, testProcedureId, title, position }) => {
      try {
        const request = new UpdateControlSuggestedTestsRequest()
          .setControlId(controlId)
          .setSuggestedTests(suggestedTests)
        if (testProcedureId) {
          request.setTestProcedureId(
            new StringValue().setValue(testProcedureId),
          )
        }
        if (title !== undefined) {
          request.setTitle(new StringValue().setValue(title))
        }
        if (position !== undefined) {
          request.setPosition(new Int32Value().setValue(position))
        }
        await client.updateControlSuggestedTests(request)
      } catch (error) {
        log.error('Error updating suggested tests', error)
        throw error
      } finally {
        await refreshAfterTPRSave()
      }
    },
    [client, refreshAfterTPRSave],
  )
}

// Refreshes every read of control-policy links: each side's list, the controls and
// policies grids that show them, a policy's requirements and audits (both found
// through its controls), and the control tests they feed.
export const useRefreshControlPolicyLinks = (): (() => Promise<void>) => {
  const invalidate = useInvalidateControlsCache()
  const invalidatePolicies = useInvalidatePoliciesCache()
  const invalidateStaleness = useInvalidateControlTestStaleness()
  const mutateMethod = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await invalidate()
    await Promise.all([
      mutateMethod(ControlServicePromiseClient.prototype.listControlPolicies),
      mutateMethod(
        ControlServicePromiseClient.prototype.batchListControlPolicies,
      ),
      mutateMethod(PolicyPromiseClient.prototype.getControls),
      mutateMethod(PolicyPromiseClient.prototype.getFrameworks),
      mutateMethod(PolicyPromiseClient.prototype.getPolicyAudits),
      invalidatePolicies(),
      invalidateStaleness(),
    ])
  }, [invalidate, invalidatePolicies, invalidateStaleness, mutateMethod])
}

// Links every control to every policy; policyIds are policy record ids, not model ids.
export const useLinkControlPolicies = (): ((args: {
  controlIds: string[]
  policyIds: string[]
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const refresh = useRefreshControlPolicyLinks()

  return useCallback(
    async ({ controlIds, policyIds }) => {
      try {
        await client.linkControlPolicies(
          new LinkControlPoliciesRequest()
            .setControlIdsList(controlIds)
            .setPolicyIdsList(policyIds),
        )
      } catch (error) {
        log.error('Error linking controls and policies', error)
        throw error
      } finally {
        await refresh()
      }
    },
    [client, refresh],
  )
}

export const useUnlinkControlPolicies = (): ((args: {
  controlIds: string[]
  policyIds: string[]
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const refresh = useRefreshControlPolicyLinks()

  return useCallback(
    async ({ controlIds, policyIds }) => {
      try {
        await client.unlinkControlPolicies(
          new UnlinkControlPoliciesRequest()
            .setControlIdsList(controlIds)
            .setPolicyIdsList(policyIds),
        )
      } catch (error) {
        log.error('Error unlinking controls and policies', error)
        throw error
      } finally {
        await refresh()
      }
    },
    [client, refresh],
  )
}

export const useUpdateControlFrameworks = (): ((args: {
  controlId: string
  frameworkIds: string[]
  complianceFrameworkIds: string[]
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, frameworkIds, complianceFrameworkIds }) => {
      try {
        const request = new UpdateControlFrameworksRequest()
          .setControlId(controlId)
          .setFrameworkIdsList(frameworkIds)
          .setComplianceFrameworkIdsList(complianceFrameworkIds)
        await client.updateControlFrameworks(request)
      } catch (error) {
        log.error('Error updating control frameworks', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlObjective = (): ((args: {
  controlId: string
  objective: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()
  const invalidateStaleness = useInvalidateControlTestStaleness()

  return useCallback(
    async ({ controlId, objective }) => {
      try {
        const request = new UpdateControlObjectiveRequest()
          .setControlId(controlId)
          .setObjective(objective)
        await client.updateControlObjective(request)
      } catch (error) {
        log.error('Error updating control objective', error)
        throw error
      } finally {
        await Promise.all([invalidate(), invalidateStaleness()])
      }
    },
    [client, invalidate, invalidateStaleness],
  )
}

export const useUpdateControlProcedure = (): ((args: {
  controlId: string
  procedure: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, procedure }) => {
      try {
        const request = new UpdateControlProcedureRequest()
          .setControlId(controlId)
          .setProcedure(procedure)
        await client.updateControlProcedure(request)
      } catch (error) {
        log.error('Error updating control procedure', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useCreateControlTestProcedure = (): ((args: {
  controlId: string
  title: string
  body: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const refreshAfterTPRSave = useRefreshAfterTPRSave()

  return useCallback(
    async ({ controlId, title, body }) => {
      try {
        const request = new CreateControlTestProcedureRequest()
          .setControlId(controlId)
          .setTitle(title)
          .setBody(body)
        await client.createControlTestProcedure(request)
      } catch (error) {
        log.error('Error creating test procedure', error)
        throw error
      } finally {
        await refreshAfterTPRSave()
      }
    },
    [client, refreshAfterTPRSave],
  )
}

export const useDeleteControlTestProcedure = (): ((args: {
  controlId: string
  testProcedureId: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const refreshAfterTPRSave = useRefreshAfterTPRSave()

  return useCallback(
    async ({ controlId, testProcedureId }) => {
      try {
        const request = new DeleteControlTestProcedureRequest()
          .setControlId(controlId)
          .setTestProcedureId(testProcedureId)
        await client.deleteControlTestProcedure(request)
      } catch (error) {
        log.error('Error deleting test procedure', error)
        throw error
      } finally {
        await refreshAfterTPRSave()
      }
    },
    [client, refreshAfterTPRSave],
  )
}

// Links one label to a control, or unlinks it, leaving its other labels as they are.
export const useSetControlLabel = (): ((args: {
  controlId: string
  labelId: string
  linked: boolean
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, labelId, linked }) => {
      if (linked) {
        await client.addControlLabel(
          new AddControlLabelRequest()
            .setControlId(controlId)
            .setLabelId(labelId),
        )
      } else {
        await client.removeControlLabel(
          new RemoveControlLabelRequest()
            .setControlId(controlId)
            .setLabelId(labelId),
        )
      }
      await invalidate()
    },
    [client, invalidate],
  )
}

export const useUpdateControlLabels = (): ((args: {
  controlId: string
  labelIds: string[]
  updateTimeSeries?: boolean
  skipGlobalControlsInvalidate?: boolean
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({
      controlId,
      labelIds,
      updateTimeSeries,
      skipGlobalControlsInvalidate,
    }) => {
      try {
        const request = new UpdateControlLabelsRequest()
          .setControlId(controlId)
          .setLabelIdsList(labelIds)
          .setUpdateTimeSeries(updateTimeSeries ?? false)
        await client.updateControlLabels(request)
      } catch (error) {
        log.error('Error updating control labels', error)
        throw error
      } finally {
        if (!skipGlobalControlsInvalidate) {
          await invalidate()
        }
      }
    },
    [client, invalidate],
  )
}
